// ============================================================================
// TableClosedOverlay - shown when a tournament table is closed/merged
// ============================================================================
// In multi-table tournaments the balancer breaks tables as players bust.
// When the current viewer's table is marked ended (or they're no longer a
// seated player at that table), surface a modal-like overlay that explains
// the table closed and routes back to the tournament view.
// ============================================================================
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Text, VStack, Button, HStack } from '@chakra-ui/react';
import { DoorClosed, Trophy } from 'lucide-react';

interface TableClosedOverlayProps {
  visible: boolean;
  tournamentId?: string;
  reason?: 'merged' | 'ended' | 'eliminated' | 'cancelled';
  finishPosition?: number | null;  // the viewer's recorded finishing place
  wonAmount?: number | null;       // payout for that place; 0 = no cash
  currency?: string;
}

const TableClosedOverlay: React.FC<TableClosedOverlayProps> = ({
  visible, tournamentId, reason = 'merged', finishPosition = null, wonAmount = null, currency = 'pkcash',
}) => {
  const navigate = useNavigate();
  if (!visible) return null;

  const moneyLabel = (n: number) => currency === 'playcoins'
    ? `${n.toLocaleString()} PC` : `$${n.toLocaleString()}`;

  const title = reason === 'eliminated'
    ? 'Eliminated'
    : reason === 'ended'
    ? 'Tournament Over'
    : reason === 'cancelled'
    ? 'Tournament Cancelled'
    : 'Table Closed';
  const message =
    reason === 'eliminated'
      ? 'You have been eliminated from the tournament.'
      : reason === 'ended'
      ? 'The tournament has ended. Head to the lobby to see results.'
      : reason === 'cancelled'
      ? 'This tournament was cancelled. Real-money entrants have been refunded their buy-in.'
      : 'Tables are being balanced - this table has been broken.';

  return (
    <Box
      position="fixed"
      top={0} left={0} right={0} bottom={0}
      bg="rgba(0,0,0,0.88)"
      zIndex={9000}
      display="flex"
      alignItems="center"
      justifyContent="center"
      p={4}
    >
      <Box
        bg="gray.900"
        border="2px solid"
        borderColor="cyan.700"
        borderRadius="lg"
        p={8}
        maxW="420px"
        w="100%"
        textAlign="center"
        boxShadow="0 20px 60px rgba(0,0,0,0.6)"
      >
        <VStack spacing={4}>
          <Box color="cyan.300">
            <DoorClosed size={48} />
          </Box>
          <Text fontSize="xl" fontWeight="bold" color="white">{title}</Text>
          <Text fontSize="sm" color="gray.400">{message}</Text>
          {(reason === 'eliminated' || reason === 'ended') && typeof finishPosition === 'number' && finishPosition > 0 && (
            <Box
              bg={(wonAmount || 0) > 0 ? 'rgba(16, 185, 129, 0.08)' : 'rgba(71, 85, 105, 0.15)'}
              border="1px solid"
              borderColor={(wonAmount || 0) > 0 ? 'rgba(16, 185, 129, 0.5)' : 'gray.600'}
              borderRadius="md"
              px={5} py={3}
              w="100%"
            >
              <Text fontSize="xs" color="gray.400" letterSpacing="wide" fontWeight="bold">
                YOU FINISHED #{finishPosition}
              </Text>
              {(wonAmount || 0) > 0 ? (
                <Text fontSize="xl" fontWeight="bold" color="#34d399">
                  You won {moneyLabel(wonAmount || 0)}
                </Text>
              ) : (
                <Text fontSize="sm" color="gray.300">Out of the money this time.</Text>
              )}
            </Box>
          )}
          <HStack spacing={3} pt={2}>
            <Button colorScheme="cyan" leftIcon={<Trophy size={16} />}
              onClick={() => {
                // Mark that the user explicitly left this tournament view so
                // the lobby's playerTableMap auto-route doesn't bounce them
                // back to the same (or replacement) table on next mount.
                try {
                  if (tournamentId) {
                    sessionStorage.setItem(`pss:leftTournament:${tournamentId}`, '1');
                  }
                } catch {/* sessionStorage may be unavailable */}
                navigate('/tournaments');
              }}>
              Tournament lobby
            </Button>
          </HStack>
        </VStack>
      </Box>
    </Box>
  );
};

export default TableClosedOverlay;
